/**
 * Mobile — Oracle spine editor helpers.
 *
 * Load, save, and diff compute for the Oracle spine data stored in
 * user_settings.oracle_spine jsonb. KYRS integration for task list
 * management on approve. Pure-logic mirroring of web src/lib/olmps/oracle.ts
 * (byte-parallel except UI layer).
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Sector } from '../domain/olmps';
import { SECTORS, SECTOR_LABELS } from '../domain/olmps';
import { SECTOR_DEITY_MAP, PRESET_ENDEAVOURS, ACTIVITY_PRESETS } from './question-banks';

/**
 * A selected Aim-stage activity: the habitual action installing the sector's
 * Goal deity. Carries the human label + cadence so KYRS task titles read as
 * real behaviors, not ids. Ledger #1.
 */
export interface OracleAim {
  id: string;
  label: string;
  cadence?: string;
}

/** One sector's spine state. */
export interface OracleSpineNode {
  endeavour: string | null;
  endeavourSource: 'manual' | 'survey' | null;
  goal: string | null; // deity id like 'sofia', 'hygia'
  /** Selected activity preset ids only — kept for back-compat + cheap diffing. */
  aimIds: string[] | null;
  /**
   * Selected activity presets with labels + cadence (ledger #1). New field;
   * older saved spines may only have `aimIds` — callers should fall back to
   * `aimIds.map(id => ({ id, label: id }))` via `effectiveAims()` below.
   */
  aims: OracleAim[] | null;
  isDirty: boolean;
}

/** Full oracle spine state for all sectors. */
export type OracleSpine = Record<Sector, OracleSpineNode>;

/** Compute initial/empty oracle spine. */
export function initialOracleSpine(): OracleSpine {
  return {
    S: { endeavour: null, endeavourSource: null, goal: null, aimIds: null, aims: null, isDirty: false },
    H: { endeavour: null, endeavourSource: null, goal: null, aimIds: null, aims: null, isDirty: false },
    A: { endeavour: null, endeavourSource: null, goal: null, aimIds: null, aims: null, isDirty: false },
    P: { endeavour: null, endeavourSource: null, goal: null, aimIds: null, aims: null, isDirty: false },
    E: { endeavour: null, endeavourSource: null, goal: null, aimIds: null, aims: null, isDirty: false },
  };
}

/**
 * Read a node's aims regardless of whether it was saved before or after the
 * `aims` field existed. Back-compat shim for ledger #1.
 */
export function effectiveAims(node: OracleSpineNode): OracleAim[] {
  if (node.aims && node.aims.length > 0) return node.aims;
  if (node.aimIds && node.aimIds.length > 0) return node.aimIds.map((id) => ({ id, label: id }));
  return [];
}

/** Get the activity presets available for a sector's currently-set Goal deity. */
export function getActivityPresetsForDeity(deityId: string | null): OracleAim[] {
  if (!deityId) return [];
  return ACTIVITY_PRESETS[deityId] || [];
}

/** Load oracle spine from user_settings via a narrow read of the oracle_spine field. */
export async function loadOracleSpine(
  supabase: SupabaseClient,
  userId: string,
): Promise<OracleSpine> {
  const { data } = await supabase
    .from('user_settings')
    .select('oracle_spine')
    .eq('user_id', userId)
    .maybeSingle();

  if (!data?.oracle_spine) {
    return initialOracleSpine();
  }

  const raw = data.oracle_spine as OracleSpine;
  // Normalize: back-fill `aims` for spines saved before ledger #1.
  const normalized = { ...raw };
  for (const sector of SECTORS) {
    const node = raw[sector];
    if (node && !node.aims) {
      const backfilled = effectiveAims(node);
      normalized[sector] = { ...node, aims: backfilled.length > 0 ? backfilled : null };
    }
  }
  return normalized;
}

/** Save oracle spine to user_settings via narrow upsert. */
export async function saveOracleSpine(
  supabase: SupabaseClient,
  userId: string,
  spine: OracleSpine,
): Promise<void> {
  await supabase.from('user_settings').upsert(
    {
      user_id: userId,
      oracle_spine: spine,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'user_id' },
  );
}

/** Compute what changed between old and new spine states (shallow comparison). */
export interface SpineDiff {
  changed: boolean;
  changedSectors: Sector[];
  oldGoal: string | null;
  newGoal: string | null;
  oldAimIds: string[] | null;
  newAimIds: string[] | null;
}

export function computeSpineDiff(oldSpine: OracleSpineNode, newSpine: OracleSpineNode): SpineDiff {
  const goalChanged = oldSpine.goal !== newSpine.goal;
  const aimChanged = JSON.stringify(oldSpine.aimIds) !== JSON.stringify(newSpine.aimIds);
  const endeavourChanged = oldSpine.endeavour !== newSpine.endeavour;

  return {
    changed: goalChanged || aimChanged || endeavourChanged,
    changedSectors: [/* will be computed per-sector */],
    oldGoal: oldSpine.goal,
    newGoal: newSpine.goal,
    oldAimIds: oldSpine.aimIds,
    newAimIds: newSpine.aimIds,
  };
}

// Re-export from question-banks for unified data source
export { SECTOR_DEITY_MAP, PRESET_ENDEAVOURS, ACTIVITY_PRESETS, type SectorDeity, type ActivityPreset } from './question-banks';

// ---------------------------------------------------------------------------
// Diff preview for KYRS integration
// ---------------------------------------------------------------------------

/** Represents added/removed aim actions for diff preview. */
export interface SpineDiffPreview {
  sector: Sector;
  addedAims: OracleAim[];
  removedAims: OracleAim[];
  kyrsGroupName: string;
}

// ---------------------------------------------------------------------------
// KYRS integration — task list management
// ---------------------------------------------------------------------------

/**
 * Get or create a KYRS task list for a sector.
 * Group name format: "SHAPE · <Sector>" (e.g., "SHAPE · Health")
 */
export async function getOrCreateKyrsListForSector(
  supabase: SupabaseClient,
  userId: string,
  sector: Sector,
): Promise<string> {
  const listName = `SHAPE · ${SECTOR_LABELS[sector]}`;

  try {
    // Try to find existing list
    const { data: existing } = await supabase
      .from('task_lists')
      .select('id')
      .eq('user_id', userId)
      .eq('name', listName)
      .maybeSingle();

    if (existing?.id) {
      return existing.id;
    }

    // Create new list
    const now = new Date().toISOString();
    const { data: newList, error } = await supabase
      .from('task_lists')
      .insert({
        user_id: userId,
        name: listName,
        is_deletable: true,
        auto_scheduler_enabled: false,
        include_in_all_tasks_view: true,
        olmps_sector: sector,
        created_at: now,
        updated_at: now,
      })
      .select('id')
      .single();

    if (error) {
      throw new Error(`Failed to create KYRS list for sector ${sector}: ${error.message}`);
    }

    return newList.id;
  } catch (err) {
    throw new Error(`Error getting/creating KYRS list: ${err instanceof Error ? err.message : String(err)}`);
  }
}

/**
 * Insert aim action tasks into a KYRS list.
 * Creates one task per aim with done=false. Task title is the human label
 * (e.g. "Prep a home-cooked meal"); cadence (e.g. "5×/week") is stored in the
 * task's `notes` field since the tasks table has no dedicated cadence column
 * (ledger #1).
 */
export async function insertAimTasks(
  supabase: SupabaseClient,
  userId: string,
  listId: string,
  aims: string[] | OracleAim[],
  sector?: Sector,
): Promise<void> {
  if (aims.length === 0) return;

  try {
    const now = new Date().toISOString();
    const tasks = aims.map((aim) => {
      const title = typeof aim === 'string' ? aim : aim.label;
      const cadence = typeof aim === 'string' ? undefined : aim.cadence;
      return {
        user_id: userId,
        list_id: listId,
        title,
        notes: cadence || null,
        done: false,
        unlisted: false,
        ...(sector ? { sector } : {}),
        // Stable Aim -> Task link (replaces title matching; SYSTEM_MAP §E P0-1).
        ...(sector && typeof aim !== 'string' ? { aim_ref: `${sector}:${aim.id}` } : {}),
        created_at: now,
        updated_at: now,
      };
    });

    const { error } = await supabase
      .from('tasks')
      .insert(tasks);

    if (error) {
      throw new Error(`Failed to insert aim tasks: ${error.message}`);
    }
  } catch (err) {
    throw new Error(`Error inserting aim tasks: ${err instanceof Error ? err.message : String(err)}`);
  }
}

/**
 * Delist aim action tasks by marking them as unlisted.
 * Searches for tasks with matching titles (labels) in the sector's KYRS list
 * — task titles are the human labels, not the internal preset ids.
 */
export async function delistAimTasks(
  supabase: SupabaseClient,
  userId: string,
  listId: string,
  aims: string[] | OracleAim[],
  sector?: Sector,
): Promise<void> {
  if (aims.length === 0) return;
  const titles = aims.map((aim) => (typeof aim === 'string' ? aim : aim.label));
  const refs = sector
    ? (aims as (string | OracleAim)[]).flatMap((aim) => (typeof aim === 'string' ? [] : [`${sector}:${aim.id}`]))
    : [];

  try {
    // Match by aim_ref first; legacy rows (no aim_ref) fall back to title.
    const ids = new Set<string>();
    if (refs.length > 0) {
      const { data: byRef, error: refError } = await supabase
        .from('tasks')
        .select('id')
        .eq('user_id', userId)
        .in('aim_ref', refs);
      if (refError) {
        throw new Error(`Failed to query tasks by aim_ref: ${refError.message}`);
      }
      for (const t of byRef ?? []) ids.add(t.id);
    }
    const { data: byTitle, error: queryError } = await supabase
      .from('tasks')
      .select('id')
      .eq('user_id', userId)
      .eq('list_id', listId)
      .is('aim_ref', null)
      .in('title', titles);

    if (queryError) {
      throw new Error(`Failed to query tasks for delisting: ${queryError.message}`);
    }
    for (const t of byTitle ?? []) ids.add(t.id);

    if (ids.size === 0) return;

    const { error: updateError } = await supabase
      .from('tasks')
      .update({ unlisted: true, updated_at: new Date().toISOString() })
      .in('id', Array.from(ids));

    if (updateError) {
      throw new Error(`Failed to delist tasks: ${updateError.message}`);
    }
  } catch (err) {
    throw new Error(`Error delisting aim tasks: ${err instanceof Error ? err.message : String(err)}`);
  }
}

/**
 * Build a diff preview showing what KYRS actions will be taken.
 * Accepts full aim objects (id + label + cadence) so the preview — and the
 * downstream insert/delist calls — carry human labels, not raw preset ids.
 */
export function buildDiffPreview(
  sector: Sector,
  oldAims: OracleAim[] | null,
  newAims: OracleAim[] | null,
): SpineDiffPreview {
  const oldById = new Map((oldAims || []).map((a) => [a.id, a]));
  const newById = new Map((newAims || []).map((a) => [a.id, a]));

  const addedAims = Array.from(newById.values()).filter((a) => !oldById.has(a.id));
  const removedAims = Array.from(oldById.values()).filter((a) => !newById.has(a.id));

  return {
    sector,
    addedAims,
    removedAims,
    kyrsGroupName: `SHAPE · ${SECTOR_LABELS[sector]}`,
  };
}
